import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import CharacterCount from '@tiptap/extension-character-count';
import Link from '@tiptap/extension-link';
import { useEffect } from 'react';
import { FONT_SIZES, FontSize } from './font-size';
import { sanitizeRichText, validateUrl } from './sanitize-and-validate';
import Heading from '@tiptap/extension-heading';
import HardBreak from '@tiptap/extension-hard-break';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  characterLimit?: number;
}

const RichTextEditor = ({ content, onChange, characterLimit = 100000 }: RichTextEditorProps): JSX.Element | null => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: {},
        bulletList: {},
        orderedList: {},
        strike: {},
        hardBreak: false,
      }),
      CharacterCount.configure({ limit: characterLimit }),
      FontSize,
      HardBreak,
      Heading.configure({
        levels: [1, 2, 3],
      }),
      TextStyle,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: 'prose font-moserrat m-5 focus:outline-none',
        dir: 'auto',
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        if (items.some((item) => item.type.indexOf('image') !== -1)) {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
    parseOptions: {
      preserveWhitespace: 'full',
    },
    content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Normalize empty content to empty string
      const normalizedHtml = html === '<p></p>' ? '' : html;
      const sanitized = sanitizeRichText(normalizedHtml);
      onChange(sanitized);
    },
  });

  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const current = editor.getHTML() === '<p></p>' ? '' : editor.getHTML();
    if (current !== content) {
      editor.commands.setContent(content || '', false);
    }
  }, [editor, content]);

  if (!editor) return null;

  return (
    <div className="rounded-md border p-4">
      <Toolbar editor={editor} />
      <div className="prose">
        <EditorContent editor={editor} />
      </div>
      <div className="mt-2 text-right text-sm text-gray-500">
        {editor.storage.characterCount.characters()} characters
      </div>
    </div>
  );
};

type ToolbarProps = {
  editor: Editor;
};

const Toolbar = ({ editor }: ToolbarProps) => {
  const buttonClass = (active: boolean) =>
    `px-2 py-1 rounded border text-sm ${active ? 'bg-gray-200 font-semibold' : 'hover:bg-gray-100'}`;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL (https://… or tel:…)', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const validationError = validateUrl(url);
    if (validationError) {
      window.alert(validationError);
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="mb-4 flex flex-wrap gap-2 border-b pb-2">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive('bold'))}
      >
        Bold
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive('italic'))}
      >
        Italic
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={buttonClass(editor.isActive('underline'))}
      >
        Underline
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={buttonClass(editor.isActive('strike'))}
      >
        Strike
      </button>
      <button type="button" onClick={setLink} className={buttonClass(editor.isActive('link'))}>
        Link
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
        className="rounded border px-2 py-1 text-sm hover:bg-gray-100"
        disabled={!editor.isActive('link')}
      >
        Unlink
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={buttonClass(editor.isActive('blockquote'))}
      >
        Blockquote
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive('bulletList'))}
      >
        Bullet List
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive('orderedList'))}
      >
        Numbered List
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setHardBreak().run()}
        className="rounded border px-2 py-1 text-sm hover:bg-gray-100"
      >
        Line Break
      </button>
      <div className="flex items-center gap-2">
        <select
          value={editor.isActive('fontSize') ? editor.getAttributes('fontSize').size || 'base' : 'base'}
          onChange={(e) => {
            if (e.target.value === 'base') {
              editor.chain().focus().unsetMark('fontSize').run();
            } else {
              editor.chain().focus().setMark('fontSize', { size: e.target.value }).run();
            }
          }}
          className="w-24 rounded border px-2 py-1 text-sm"
        >
          {FONT_SIZES.map((size) => (
            <option key={size.value} value={size.value}>
              {size.name}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        className={buttonClass(editor.isActive('heading', { level: 1 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        H1
      </button>
      <button
        type="button"
        className={buttonClass(editor.isActive('heading', { level: 2 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        H2
      </button>
      <button
        type="button"
        className={buttonClass(editor.isActive('heading', { level: 3 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        H3
      </button>
    </div>
  );
};

export default RichTextEditor;
